import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router";
import { supabase } from "@/integrations/supabase/client";
import { FieldRulesList } from "@/components/fields/rules/FieldRulesList";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";

const FieldRules = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const fieldIdFromUrl = searchParams.get("fieldId");
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(fieldIdFromUrl);

  // Fetch the fields for dropdown selection
  const { data: fields, isLoading } = useQuery({
    queryKey: ["fields-for-rules"],
    queryFn: async () => {
      const { data, error } = await supabase.from("fields").select("id, name").order("name");

      if (error) throw error;
      return data;
    },
  });

  // Update selected field when URL parameter changes
  useEffect(() => {
    if (fieldIdFromUrl) {
      setSelectedFieldId(fieldIdFromUrl);
    }
  }, [fieldIdFromUrl]);

  // Update the URL when selected field changes
  const handleFieldChange = (fieldId: string) => {
    setSelectedFieldId(fieldId);
    setSearchParams({ fieldId });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/admin/fields">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Field Rules</h1>
          <p className="text-muted-foreground">Create and manage rules for fields to improve OpenAI prompt accuracy</p>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="max-w-md mb-6">
            <label className="block mb-2 text-sm font-medium text-gray-700">Select a field to manage rules</label>
            <Select value={selectedFieldId || ""} onValueChange={handleFieldChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select a field" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {isLoading ? (
                    <SelectItem value="loading" disabled>
                      Loading fields...
                    </SelectItem>
                  ) : (
                    fields?.map((field) => (
                      <SelectItem key={field.id} value={field.id}>
                        {field.name}
                      </SelectItem>
                    ))
                  )}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          {selectedFieldId ? (
            <>
              {fields && (
                <h2 className="text-xl font-medium mb-4">
                  Rules for: {fields.find((f) => f.id === selectedFieldId)?.name || "Selected Field"}
                </h2>
              )}
              <FieldRulesList fieldId={selectedFieldId} />
            </>
          ) : (
            <div className="text-center p-8 text-muted-foreground">
              Please select a field to view and manage its rules.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FieldRules;
